import type {JSX} from 'react'
import {useState, useEffect} from 'react'
import {Calendar} from 'react-calendar'
import {clsx} from 'clsx'
import type {Variant} from '@/_new-code/products/flexible-web-toolkit/components/button/ellipse-button'
import {EllipseButton} from '@/_new-code/products/flexible-web-toolkit/components/button/ellipse-button'
import {ButtonIcons} from '@/_new-code/components/button-icons/button-icon'

interface CalendarBlockProps {
	dates?: string[]
	className?: string
	onClick?: (date: Date) => void
	iconActiveColor?: string
	activeDateColor?: string
	btnVariant?: string
	hideNeighboringMonth?: boolean
	progressHandler?: (activeStartDate: Date, value: Date, view: string) => void
	selectedDate?: Date
}

export const CalendarBlock = ({
	dates = [],
	className = '',
	onClick = () => {
		// Default behavior for onClick
	},
	progressHandler = () => {
		// Default behavior for progressHandler
	},
	iconActiveColor = 'border-primary',
	activeDateColor = 'bg-blue-100',
	btnVariant = 'primary',
	hideNeighboringMonth = false,
	selectedDate = new Date(),
}: CalendarBlockProps): JSX.Element => {
	const [activeDate, setActiveDate] = useState<string>(
		selectedDate.toUTCString()
	)

	useEffect(() => {
		setActiveDate(selectedDate.toUTCString())
	}, [selectedDate])

	// Calculates the timezone offset and adjusts accordingly relative to local time
	const getTimezoneDate = (date: Date): Date => {
		const tz = date.getTimezoneOffset()
		const hours = date.getHours()
		const mins = date.getMinutes()
		const tzHours = tz / 60
		const tzMins = tz % 60
		if (tz > 0) date.setHours(hours + tzHours, mins + tzMins)
		return date
	}

	// If there is a change between current date and new date, the function attempts to find an event date within the same month and year from the dates list.
	const setActiveDateForMonthYear = (
		newDate: Date,
		currentDate: Date
	): void => {
		// only setActiveDate if the month or year is changed
		if (
			newDate.getMonth() !== currentDate.getMonth() ||
			newDate.getFullYear() !== currentDate.getFullYear()
		) {
			const nextEventDate = dates.find((dtStr) => {
				const dt = new Date(dtStr)
				return (
					dt.getMonth() === newDate.getMonth() &&
					dt.getFullYear() === newDate.getFullYear()
				)
			})
			if (nextEventDate) {
				setActiveDate(new Date(nextEventDate).toUTCString())
				onClick(getTimezoneDate(new Date(nextEventDate)))
			} else {
				setActiveDate(newDate.toUTCString())
			}
		}
	}

	const addClassNames = (date: Date): string => {
		const classValue = []
		const setDate = date.toLocaleDateString()
		const currentDate = new Date().toLocaleDateString()

		setDate === currentDate
			? classValue.push(
					`${activeDateColor} rounded-md outline-none today`
				)
			: null

		dates.some(
			(d) => setDate === getTimezoneDate(new Date(d)).toLocaleDateString()
		)
			? classValue.push('relative has-reminder')
			: null
		return clsx(classValue.join(' '))
	}

	const addTileContent = (date: Date): JSX.Element | null => {
		if (
			dates.find(
				(d) =>
					date.toLocaleDateString() ===
					getTimezoneDate(new Date(d)).toLocaleDateString()
			)
		) {
			return (
				<div
					className={`absolute inset-0 z-0 m-auto h-10 w-10 rounded-full border-3 ${iconActiveColor} sm:h-12 sm:w-12`}
				/>
			)
		}
		return null
	}

	return (
		<div className={className}>
			<Calendar
				activeStartDate={new Date(activeDate)}
				className="p-4"
				defaultValue={new Date(activeDate)}
				maxDetail="month"
				next2Label={null}
				nextLabel={
					<EllipseButton
						icon={ButtonIcons.ArrowRight}
						noIconAnimation
						size="sm"
						variant={btnVariant as Variant}
					>
						Next Month
					</EllipseButton>
				}
				onActiveStartDateChange={({
					activeStartDate,
					value,
					view,
				}: {
					activeStartDate: Date
					value: Date
					view: string
				}) => {
					setActiveDateForMonthYear(activeStartDate, value)
					progressHandler(activeStartDate, value, view)
				}}
				onClickDay={(value: Date) => {
					setActiveDate(value.toUTCString())
					onClick(value)
				}}
				prev2Label={null}
				prevLabel={
					<EllipseButton
						icon={ButtonIcons.ArrowLeft}
						noIconAnimation
						size="sm"
						variant={btnVariant as Variant}
					>
						Previous Month
					</EllipseButton>
				}
				showNeighboringMonth={!hideNeighboringMonth}
				tileClassName={({date}: {date: Date}) => addClassNames(date)}
				tileContent={({date}: {date: Date}) => addTileContent(date)}
				value={new Date(activeDate)}
				view="month"
			/>
		</div>
	)
}
